/**
 * Chat Mode Redux Slice (Issue #302)
 *
 * Manages chat mode state for the mode switching system.
 * Supports: help, settings, ask, plan, drbfm modes.
 */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/index';
import {
  ChatMode,
  ModeListResponse,
  SwitchModeRequest,
  SwitchModeResponse,
  ThreadModeResponse,
} from '../../types/chat';
import chatService from '../../services/chatService';

// ============================================================
// State Interface
// ============================================================

interface ChatModeState {
  // Available modes
  modes: ChatMode[];
  modesLoading: boolean;
  modesError: string | null;

  // Current mode
  currentMode: string;
  currentModeData: Record<string, any>;
  previousMode: string | null;

  // UI state
  showModeSelector: boolean;
  showSlashSuggestions: boolean;
  slashSuggestionQuery: string;

  // Language suggestion (for auto-detection)
  languageSuggestion: string | null;

  // Mode switching state
  modeSwitching: boolean;
  modeSwitchError: string | null;
}

// ============================================================
// Initial State
// ============================================================

const initialState: ChatModeState = {
  modes: [
    {
      id: 'help',
      name: 'ヘルプ',
      description: 'システムの使い方とコマンド一覧',
      color: '#4caf50',
      icon: 'help',
      isActive: true,
      isCustom: false,
      sortOrder: 10,
      settings: { temperature: 0.3, max_tokens: 1000 },
    },
    {
      id: 'settings',
      name: '設定',
      description: 'データソース状態・利用統計',
      color: '#ff9800',
      icon: 'settings',
      isActive: true,
      isCustom: false,
      sortOrder: 20,
      settings: { temperature: 0.2, max_tokens: 1500 },
    },
    {
      id: 'ask',
      name: 'Q&A',
      description: '質問応答モード（デフォルト）',
      color: '#1976d2',
      icon: 'question_answer',
      isActive: true,
      isCustom: false,
      sortOrder: 30,
      settings: { temperature: 0.7, max_tokens: 1500 },
    },
    {
      id: 'plan',
      name: '設計深掘り',
      description: '設計に関する深い議論',
      color: '#9c27b0',
      icon: 'architecture',
      isActive: true,
      isCustom: false,
      sortOrder: 40,
      settings: { temperature: 0.5, max_tokens: 2000 },
    },
    {
      id: 'drbfm',
      name: 'DRBFM分析',
      description: 'DRBFM分析サポート',
      color: '#f44336',
      icon: 'assessment',
      isActive: true,
      isCustom: false,
      sortOrder: 50,
      settings: { temperature: 0.4, max_tokens: 2000 },
    },
    {
      id: 'verification',
      name: '設計検証',
      description: '設計検証項目の分析・改善支援',
      color: '#00897b',
      icon: 'verified',
      isActive: true,
      isCustom: false,
      sortOrder: 55,
      settings: { temperature: 0.4, max_tokens: 2500 },
    },
    {
      id: 'cad',
      name: 'CAD分析',
      description: 'CADデータの構造・設計レビュー',
      color: '#607d8b',
      icon: 'view_in_ar',
      isActive: true,
      isCustom: false,
      sortOrder: 60,
      settings: { temperature: 0.5, max_tokens: 2000 },
    },
  ],
  modesLoading: false,
  modesError: null,

  currentMode: localStorage.getItem('aiona_default_chat_mode') || 'ask',
  currentModeData: {},
  previousMode: null,

  showModeSelector: false,
  showSlashSuggestions: false,
  slashSuggestionQuery: '',

  languageSuggestion: null,

  modeSwitching: false,
  modeSwitchError: null,
};

// ============================================================
// Async Thunks
// ============================================================

/**
 * Fetch available modes from API
 */
export const fetchModes = createAsyncThunk<ModeListResponse, void>(
  'chatMode/fetchModes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.getModes();
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch modes'
      );
    }
  }
);

/**
 * Switch mode for a thread
 */
export const switchMode = createAsyncThunk<
  SwitchModeResponse,
  { threadId: string; request: SwitchModeRequest }
>(
  'chatMode/switchMode',
  async ({ threadId, request }, { rejectWithValue }) => {
    try {
      const response = await chatService.switchMode(threadId, request);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to switch mode'
      );
    }
  }
);

/**
 * Get current mode for a thread
 */
export const fetchThreadMode = createAsyncThunk<ThreadModeResponse, string>(
  'chatMode/fetchThreadMode',
  async (threadId, { rejectWithValue }) => {
    try {
      const response = await chatService.getThreadMode(threadId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to get thread mode'
      );
    }
  }
);

// ============================================================
// Slice
// ============================================================

const chatModeSlice = createSlice({
  name: 'chatMode',
  initialState,
  reducers: {
    // Mode selection UI
    setShowModeSelector: (state, action: PayloadAction<boolean>) => {
      state.showModeSelector = action.payload;
    },
    toggleModeSelector: (state) => {
      state.showModeSelector = !state.showModeSelector;
    },

    // Slash command suggestions
    setShowSlashSuggestions: (state, action: PayloadAction<boolean>) => {
      state.showSlashSuggestions = action.payload;
    },
    setSlashSuggestionQuery: (state, action: PayloadAction<string>) => {
      state.slashSuggestionQuery = action.payload;
      // Show suggestions if query starts with '/'
      state.showSlashSuggestions = action.payload.startsWith('/');
    },
    clearSlashSuggestions: (state) => {
      state.showSlashSuggestions = false;
      state.slashSuggestionQuery = '';
    },

    // Direct mode switching (without API call)
    setCurrentMode: (state, action: PayloadAction<string>) => {
      state.previousMode = state.currentMode;
      state.currentMode = action.payload;
      // Clear mode-specific data when switching modes (Bugbot fix)
      state.currentModeData = {};
      // Persist to localStorage so reload restores last mode (#494 B-3)
      localStorage.setItem('aiona_default_chat_mode', action.payload);
    },

    // Alias kept for explicit "set as default" intent
    setDefaultMode: (state, action: PayloadAction<string>) => {
      state.previousMode = state.currentMode;
      state.currentMode = action.payload;
      state.currentModeData = {};
      localStorage.setItem('aiona_default_chat_mode', action.payload);
    },
    setModeData: (state, action: PayloadAction<Record<string, any>>) => {
      state.currentModeData = action.payload;
    },
    updateModeData: (
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) => {
      state.currentModeData[action.payload.key] = action.payload.value;
    },
    clearModeData: (state) => {
      state.currentModeData = {};
    },

    // Language suggestion
    setLanguageSuggestion: (state, action: PayloadAction<string | null>) => {
      state.languageSuggestion = action.payload;
    },

    // Reset mode to default
    resetMode: (state) => {
      state.previousMode = state.currentMode;
      state.currentMode = 'ask';
      state.currentModeData = {};
      state.showModeSelector = false;
      state.showSlashSuggestions = false;
      state.slashSuggestionQuery = '';
    },

    // Clear errors
    clearModeSwitchError: (state) => {
      state.modeSwitchError = null;
    },
    clearModesError: (state) => {
      state.modesError = null;
    },
  },
  extraReducers: (builder) => {
    // fetchModes
    builder
      .addCase(fetchModes.pending, (state) => {
        state.modesLoading = true;
        state.modesError = null;
      })
      .addCase(fetchModes.fulfilled, (state, action) => {
        state.modesLoading = false;
        state.modes = action.payload.modes;
      })
      .addCase(fetchModes.rejected, (state, action) => {
        state.modesLoading = false;
        state.modesError = action.payload as string;
      });

    // switchMode
    builder
      .addCase(switchMode.pending, (state) => {
        state.modeSwitching = true;
        state.modeSwitchError = null;
      })
      .addCase(switchMode.fulfilled, (state, action) => {
        state.modeSwitching = false;
        state.previousMode = action.payload.previous_mode || state.currentMode;
        state.currentMode = action.payload.mode.id;
        state.showModeSelector = false;
        // Clear mode-specific data when switching modes (Bugbot fix)
        state.currentModeData = {};
        // Persist to localStorage (#494 B-3)
        localStorage.setItem('aiona_default_chat_mode', action.payload.mode.id);
      })
      .addCase(switchMode.rejected, (state, action) => {
        state.modeSwitching = false;
        state.modeSwitchError = action.payload as string;
      });

    // fetchThreadMode
    builder
      .addCase(fetchThreadMode.pending, (state) => {
        state.modesLoading = true;
      })
      .addCase(fetchThreadMode.fulfilled, (state, action) => {
        state.modesLoading = false;
        state.currentMode = action.payload.current_mode;
      })
      .addCase(fetchThreadMode.rejected, (state) => {
        state.modesLoading = false;
        // Default to 'ask' if failed to get thread mode
        state.currentMode = 'ask';
      });
  },
});

// ============================================================
// Actions
// ============================================================

export const {
  setShowModeSelector,
  toggleModeSelector,
  setShowSlashSuggestions,
  setSlashSuggestionQuery,
  clearSlashSuggestions,
  setCurrentMode,
  setDefaultMode,
  setModeData,
  updateModeData,
  clearModeData,
  setLanguageSuggestion,
  resetMode,
  clearModeSwitchError,
  clearModesError,
} = chatModeSlice.actions;

// ============================================================
// Selectors
// ============================================================

// Mode list selectors
export const selectModes = (state: RootState) => state.chatMode.modes;
export const selectActiveModes = (state: RootState) =>
  state.chatMode.modes.filter((m) => m.isActive);
export const selectModesLoading = (state: RootState) =>
  state.chatMode.modesLoading;
export const selectModesError = (state: RootState) => state.chatMode.modesError;

// Current mode selectors
export const selectCurrentMode = (state: RootState) =>
  state.chatMode.currentMode;
export const selectCurrentModeData = (state: RootState) =>
  state.chatMode.currentModeData;
export const selectPreviousMode = (state: RootState) =>
  state.chatMode.previousMode;
export const selectCurrentModeConfig = (state: RootState) =>
  state.chatMode.modes.find((m) => m.id === state.chatMode.currentMode);

// UI state selectors
export const selectShowModeSelector = (state: RootState) =>
  state.chatMode.showModeSelector;
export const selectShowSlashSuggestions = (state: RootState) =>
  state.chatMode.showSlashSuggestions;
export const selectSlashSuggestionQuery = (state: RootState) =>
  state.chatMode.slashSuggestionQuery;

// Mode switching selectors
export const selectModeSwitching = (state: RootState) =>
  state.chatMode.modeSwitching;
export const selectModeSwitchError = (state: RootState) =>
  state.chatMode.modeSwitchError;

// Language suggestion selector
export const selectLanguageSuggestion = (state: RootState) =>
  state.chatMode.languageSuggestion;

// ============================================================
// Export
// ============================================================

export default chatModeSlice.reducer;
